import { faCalendar } from "@fortawesome/free-regular-svg-icons/faCalendar";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome"
import { Dialog } from "@mui/material"
import { PaperArrastavel } from "app/components/dialog-arrastavel/PaperArrastavel"
import { DataFormatoEN } from "app/types/DataFormatoEN"
import { DataFormatoPT } from "app/types/DataFormatoPT"
import globalMU from "globalMU"
import React, { useEffect, useState, useRef, useCallback } from "react"
import InputMask from '../../libs/reactInputMask'
import root from 'react-shadow'
import { Calendario, CLASSE_DIALOG_CALENDARIO } from "../calendario/Calendarios"
import { parse } from "date-fns"
import * as R from 'ramda'

type InputDataCalendarioProps = {
    onChange: (data: DataFormatoEN) => void
    data: DataFormatoPT
    bloquearDataMenorQueHoje?: boolean
    dataMinima?: { year: number, month: number, day: number }
    dataMaxima?: { year: number, month: number, day: number }
    className?: string
    selecionaValorOnFocus?: boolean
    disabled?: boolean
    id?: string
}

export const InputDataCalendario = (props: InputDataCalendarioProps) => {
    const idCounter = useRef(0)
    const generatedId = `InputDataCalendario_${idCounter.current++}`
    const inputId = props.id || generatedId
    const iconId = `${inputId}_icon`
    
    const [keyDown, setKeyDown] = useState(false)
    const [touch, setTouch] = useState(false)
    const [data, setData] = useState(props.data ?? "")
    const [mostrarCalendario, setMostrarCalendario] = useState(false)
    const isMounted = useRef(true)

    useEffect(() => {
        return () => { isMounted.current = false }
    }, [])

    useEffect(() => {
        if (isMounted.current) {
            setData(props.data ?? "")
        }
    }, [props.data])

    const handleKeyDown = useCallback((e: React.KeyboardEvent<HTMLInputElement>) => {
        setKeyDown(true)

        if (R.includes(e.code, ["Backspace"]) && e.currentTarget.selectionStart === 0 && e.currentTarget.selectionEnd === 10) {
            setData("")
        }
    }, [])

    const handleChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
        if (!isMounted.current) return

        setData(e.target.value)
        setTouch(true)
        
        if (keyDown) return

        if (globalMU.dataNoFormatoPTEhValida(e.target.value) && e.target.value !== data) {
            props.onChange(globalMU.converterDataFormatoPTParaEN(e.target.value))
            setData(e.target.value)
        }

        if (!globalMU.dataNoFormatoPTEhValida(e.target.value)) {
            props.onChange(null)
            setData("")
        }

        setKeyDown(false)
    }, [keyDown, data, props.onChange])

    const handleBlur = useCallback((e: React.FocusEvent<HTMLInputElement>) => {
        if (!isMounted.current) return

        if (keyDown) {
            setKeyDown(false)
            
            if (globalMU.dataNoFormatoPTEhValida(e.target.value) && touch) {
                props.onChange(globalMU.converterDataFormatoPTParaEN(e.target.value))
                setData(e.target.value)
            }

            if (!globalMU.dataNoFormatoPTEhValida(e.target.value)) {
                props.onChange(null)
                setData("")
            }

            if (globalMU.removerMascara("99/99/9999", e.target.value, true).length === 4) {
                const dataString = globalMU.removerMascara("99/99/9999", e.target.value, true)
                const dataPT = `${dataString.substring(0, 2)}/${dataString.substring(2, 4)}/${new Date().getFullYear()}`
                setData(dataPT)
                props.onChange(globalMU.converterDataFormatoPTParaEN(dataPT))
            }

            setTouch(false)
        }
    }, [keyDown, touch, props.onChange])

    const handleFocus = useCallback((e: React.FocusEvent<HTMLInputElement>) => {
        if (R.isNil(props.selecionaValorOnFocus) || props.selecionaValorOnFocus) {
            e.target.select()
        }
    }, [props.selecionaValorOnFocus])

    const handleCalendarClick = useCallback(() => {
        setMostrarCalendario(true)
    }, [])

    const handleCalendarClose = useCallback(() => {
        setMostrarCalendario(false)
    }, [])

    const handleDateSelection = useCallback((dataModificada: Date) => {
        if (!isMounted.current) return

        props.onChange(globalMU.converterObjetoDateParaDataFormatoEN(dataModificada))
        setData(globalMU.converterObjetoDateParaDataFormatoPT(dataModificada))
        setMostrarCalendario(false)
    }, [props.onChange])

    return (
        <>
            <div className="_flex _flex-row _w-[120px]"
                    key={`${inputId}_div`}
                    id={`${inputId}_div`}       
            >
                <InputMask
                    mask="99/99/9999"
                    key={inputId}
                    id={inputId}
                    className={props.className}
                    disabled={props.disabled}
                    onKeyDown={handleKeyDown}
                    onChange={handleChange}
                    onBlur={handleBlur}
                    onFocus={handleFocus}
                    value={data}
                    data-testid={inputId}
                />

                {!props.disabled && (
                    <FontAwesomeIcon
                        cursor="pointer"
                        className="_w-3 material-icons"
                        key={iconId}
                        id={iconId}
                        icon={faCalendar}
                        style={{ marginLeft: "-20px" }}
                        onClick={handleCalendarClick}
                        data-testid={iconId}
                    />
                )}
            </div>

            {mostrarCalendario && (
                <Dialog open={mostrarCalendario} onClose={handleCalendarClose}
                    key={`${inputId}_dialog`}
                    id={`${inputId}_dialog`}
                    PaperComponent={PaperArrastavel}
                    className={CLASSE_DIALOG_CALENDARIO}
                >
                    <root.div>
                        <Calendario
                            key={`${inputId}_Calendario`}
                            id={`${inputId}_Calendario`}
                            dataMinima={props.bloquearDataMenorQueHoje ? { year: new Date().getFullYear(), month: new Date().getMonth() + 1, day: new Date().getDate() } : props.dataMinima}
                            dataMaxima={props.dataMaxima}
                            data={globalMU.dataNoFormatoPTEhValida(data) ? parse(data, "dd/MM/yyyy", new Date()) : new Date()}
                            onDataModificado={handleDateSelection}
                        />
                    </root.div>
                </Dialog>
            )}
        </>
    )
}
